import { zodResolver } from "@hookform/resolvers/zod";
import type { Employee, JobOpening } from "@shared/schema";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  AlertCircle,
  AlertTriangle,
  Briefcase,
  Check,
  Clock,
  Copy,
  Download,
  ExternalLink,
  Loader2,
  MoreHorizontal,
  Pin,
  Plus,
  Search,
  Star,
  Trash2,
  UserRound,
  Users,
  X,
} from "lucide-react";
import { useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { useLocation } from "wouter";
import { z } from "zod";
import { DepartmentIcon } from "@/components/department-icon";
import { InfoCard } from "@/components/info-card";
import { PageHeader } from "@/components/page-header";
import { StatusBadge } from "@/components/status-badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import {
  DEPARTMENTS,
  formatCurrency,
  formatDate,
  getDepartmentLabel,
  getJobLevelLabel,
  JOB_LEVELS,
} from "@/lib/constants";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { orpc, rpcClient } from "@/lib/rpc";
import { matchesSearch } from "@/lib/search-utils";
import { cn } from "@/lib/utils";

type NotionPosition = {
  notionId: string;
  jobTitle: string | null;
  hiringManagerEmail: string | null;
  jdLink: string | null;
  stages: any[];
  linkedJob: { id: string; title: string; status: string; department: string } | null;
  syncState: { status: string; syncedAt: string } | null;
};

type JobsSyncResponse = {
  notionPositions: NotionPosition[];
  localJobs: {
    id: string;
    title: string;
    status: string;
    department: string;
    notionId: string | null;
    hiringManagerName: string | null;
  }[];
};

const interviewerGroupSchema = z.object({
  id: z.string().optional(),
  employeeIds: z.array(z.string()).min(2, "Grupo precisa de pelo menos 2 pessoas"),
  schedulingLink: z.string().nullable().default(null),
});

const interviewStageSchema = z.object({
  stageName: z.string().min(1, "Stage name is required"),
  stageOrder: z.number().min(1),
  durationMinutes: z.number().min(15, "Minimum 15 minutes"),
  defaultInterviewerId: z.string().optional(),
  interviewerIds: z.array(z.string()).default([]),
  interviewerGroups: z.array(interviewerGroupSchema).default([]),
});

const jobFormSchema = z.object({
  title: z.string().min(2, "Title must be at least 2 characters"),
  generalResponsibilities: z
    .string()
    .min(10, "Responsabilidades deve ter pelo menos 10 caracteres"),
  desiredProfile: z.string().min(10, "Perfil desejado deve ter pelo menos 10 caracteres"),
  compensation: z.string().min(1, "Remuneração é obrigatória"),
  jobLevel: z.string().min(1, "Level is required"),
  department: z.string().min(1, "Department is required"),
  hiringManagerId: z.string().min(1, "Hiring manager is required"),
  salaryExpected: z.string().min(1, "Salary is required"),
  interviewStages: z.array(interviewStageSchema).min(1, "At least one interview stage is required"),
});

type JobFormValues = z.infer<typeof jobFormSchema>;

const defaultStages = [
  {
    stageName: "Screening",
    stageOrder: 1,
    durationMinutes: 30,
    defaultInterviewerId: "",
    interviewerIds: [] as string[],
    interviewerGroups: [] as { employeeIds: string[]; schedulingLink: string | null }[],
  },
  {
    stageName: "Technical",
    stageOrder: 2,
    durationMinutes: 60,
    defaultInterviewerId: "",
    interviewerIds: [] as string[],
    interviewerGroups: [] as { employeeIds: string[]; schedulingLink: string | null }[],
  },
  {
    stageName: "Cultural Fit",
    stageOrder: 3,
    durationMinutes: 45,
    defaultInterviewerId: "",
    interviewerIds: [] as string[],
    interviewerGroups: [] as { employeeIds: string[]; schedulingLink: string | null }[],
  },
  {
    stageName: "Founder",
    stageOrder: 4,
    durationMinutes: 30,
    defaultInterviewerId: "",
    interviewerIds: [] as string[],
    interviewerGroups: [] as { employeeIds: string[]; schedulingLink: string | null }[],
  },
];

function NotionImportDialog({
  open,
  onOpenChange,
  positions,
  isLoading,
  importingNotionId,
  isPending,
  onImport,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  positions?: NotionPosition[];
  isLoading: boolean;
  importingNotionId: string | null;
  isPending: boolean;
  onImport: (notionId: string) => void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Importar Vaga do Notion</DialogTitle>
          <DialogDescription>
            Selecione uma posição do Notion para importar como vaga no sistema.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {isLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="space-y-2">
                    <Skeleton className="h-4 w-48" />
                    <Skeleton className="h-3 w-32" />
                  </div>
                  <Skeleton className="h-8 w-20" />
                </div>
              ))}
            </div>
          ) : !positions?.length ? (
            <div className="text-center py-8 text-muted-foreground">
              <AlertCircle className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm" data-testid="text-notion-empty">
                Nenhuma posição encontrada no Notion.
              </p>
              <p className="text-xs mt-1">
                Verifique se a integração com o Notion está configurada corretamente.
              </p>
            </div>
          ) : (
            positions.map((position) => {
              const isImported = !!position.linkedJob;
              const isImporting = importingNotionId === position.notionId && isPending;
              return (
                <div
                  key={position.notionId}
                  className={`flex items-center justify-between p-3 border rounded-lg transition-shadow ${isImported ? "bg-muted/30 opacity-70" : "hover:shadow-sm"}`}
                  data-testid={`notion-position-${position.notionId}`}
                >
                  <div className="flex-1 min-w-0 mr-3">
                    <div className="flex items-center gap-2">
                      <p
                        className="font-medium text-sm truncate"
                        data-testid={`text-notion-title-${position.notionId}`}
                      >
                        {position.jobTitle || "Sem título"}
                      </p>
                      {isImported && (
                        <Badge variant="secondary" className="text-xs shrink-0">
                          Já importada
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-3 mt-1">
                      {position.hiringManagerEmail && (
                        <p
                          className="text-xs text-muted-foreground truncate"
                          data-testid={`text-notion-manager-${position.notionId}`}
                        >
                          {position.hiringManagerEmail}
                        </p>
                      )}
                      {position.stages.length > 0 && (
                        <p
                          className="text-xs text-muted-foreground shrink-0"
                          data-testid={`text-notion-stages-${position.notionId}`}
                        >
                          {position.stages.length} etapa{position.stages.length !== 1 ? "s" : ""}
                        </p>
                      )}
                      {isImported && position.linkedJob && (
                        <p className="text-xs text-muted-foreground shrink-0">
                          {position.linkedJob.department}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {position.jdLink && (
                      <Button variant="ghost" size="sm" asChild>
                        <a
                          href={position.jdLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`link-notion-jd-${position.notionId}`}
                        >
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      </Button>
                    )}
                    <Button
                      size="sm"
                      disabled={isImported || isImporting}
                      onClick={() => onImport(position.notionId)}
                      data-testid={`button-import-notion-${position.notionId}`}
                    >
                      {isImporting ? (
                        <>
                          <Loader2 className="h-3 w-3 mr-1 animate-spin" /> Importando...
                        </>
                      ) : isImported ? (
                        <>
                          <Check className="h-3 w-3 mr-1" /> Importada
                        </>
                      ) : (
                        <>
                          <Download className="h-3 w-3 mr-1" /> Importar
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Jobs() {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [interviewerSearch, setInterviewerSearch] = useState<Record<number, string>>({});
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectJobId, setRejectJobId] = useState<string | null>(null);
  const [rejectionReason, setRejectionReason] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteJobData, setDeleteJobData] = useState<{ id: string; title: string } | null>(null);
  const [notionImportOpen, setNotionImportOpen] = useState(false);
  const [importingNotionId, setImportingNotionId] = useState<string | null>(null);
  const [isDuplicating, setIsDuplicating] = useState<string | null>(null);
  const { toast } = useToast();
  const { hasPermission, isExternal } = useAuth();
  const canApproveJobs = hasPermission("approve_jobs_offers");
  const [, setLocation] = useLocation();

  type JobWithManager = JobOpening & { hiringManager?: Employee; candidateCount?: number };

  type JobWithFavorites = JobWithManager & {
    isFavorited: boolean;
    isPinned: boolean;
  };

  const { data: jobs = [], isLoading } = useQuery(orpc.jobOpenings.list.queryOptions());

  const { data: people } = useQuery(orpc.dashboard.people.queryOptions());

  const { data: notionJobs, isLoading: notionJobsLoading } = useQuery<JobsSyncResponse>({
    queryKey: ["/api/sync/notion/jobs"],
    enabled: notionImportOpen,
  });

  const importNotionJob = useMutation({
    mutationFn: async (notionId: string) => {
      setImportingNotionId(notionId);
      return apiRequest("POST", `/api/sync/notion/jobs/${notionId}/import`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: orpc.jobOpenings.list.queryOptions().queryKey });
      queryClient.invalidateQueries({ queryKey: ["/api/sync/notion/jobs"] });
      toast({
        title: "Vaga importada com sucesso",
        description: "A vaga foi criada a partir do Notion.",
      });
      setImportingNotionId(null);
    },
    onError: (error: any) => {
      const message = error?.message || "Não foi possível importar a vaga.";
      toast({ title: "Erro ao importar", description: message, variant: "destructive" });
      setImportingNotionId(null);
    },
  });

  const jobListQueryKey = orpc.jobOpenings.list.queryOptions().queryKey;

  const pinnedQueryKey = orpc.jobFavorites.listPinned.queryOptions().queryKey;

  const toggleFavorite = useMutation({
    mutationFn: async ({ jobId, isFavorited }: { jobId: string; isFavorited: boolean }) => {
      if (isFavorited) {
        return rpcClient.jobFavorites.unfavorite({ jobOpeningId: jobId });
      }
      return rpcClient.jobFavorites.favorite({ jobOpeningId: jobId });
    },
    onMutate: async ({ jobId, isFavorited }) => {
      await queryClient.cancelQueries({ queryKey: jobListQueryKey });
      const previous = queryClient.getQueryData<JobWithFavorites[]>(jobListQueryKey);
      queryClient.setQueryData<JobWithFavorites[]>(jobListQueryKey, (old) =>
        old?.map((j) =>
          j.id === jobId
            ? { ...j, isFavorited: !isFavorited, isPinned: isFavorited ? false : j.isPinned }
            : j,
        ),
      );
      return { previous };
    },
    onError: (_err, _vars, context) => {
      queryClient.setQueryData(jobListQueryKey, context?.previous as any);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: jobListQueryKey });
      queryClient.invalidateQueries({ queryKey: pinnedQueryKey });
    },
  });

  const togglePin = useMutation({
    mutationFn: async ({ jobId, isPinned }: { jobId: string; isPinned: boolean }) => {
      if (isPinned) {
        return rpcClient.jobFavorites.unpin({ jobOpeningId: jobId });
      }
      return rpcClient.jobFavorites.pin({ jobOpeningId: jobId });
    },
    onMutate: async ({ jobId, isPinned }) => {
      await queryClient.cancelQueries({ queryKey: jobListQueryKey });
      const previous = queryClient.getQueryData<JobWithFavorites[]>(jobListQueryKey);
      queryClient.setQueryData<JobWithFavorites[]>(jobListQueryKey, (old) =>
        old?.map((j) =>
          j.id === jobId
            ? { ...j, isPinned: !isPinned, isFavorited: isPinned ? j.isFavorited : true }
            : j,
        ),
      );
      return { previous };
    },
    onError: (_err, _vars, context) => {
      queryClient.setQueryData(jobListQueryKey, context?.previous as any);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: jobListQueryKey });
      queryClient.invalidateQueries({ queryKey: pinnedQueryKey });
    },
  });

  const form = useForm<JobFormValues>({
    resolver: zodResolver(jobFormSchema),
    defaultValues: {
      title: "",
      generalResponsibilities: "",
      desiredProfile: "",
      compensation: "",
      jobLevel: "L2",
      department: "engineering",
      hiringManagerId: "",
      salaryExpected: "",
      interviewStages: defaultStages,
    },
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "interviewStages",
  });

  const createJob = useMutation({
    mutationFn: async (data: JobFormValues) => {
      return rpcClient.jobOpenings.create(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: orpc.jobOpenings.list.queryOptions().queryKey });
      setIsOpen(false);
      form.reset({
        title: "",
        generalResponsibilities: "",
        desiredProfile: "",
        compensation: "",
        jobLevel: "L2",
        department: "engineering",
        hiringManagerId: "",
        salaryExpected: "",
        interviewStages: defaultStages,
      });
      toast({ title: "Vaga criada com sucesso", description: "A vaga está aguardando aprovação." });
    },
    onError: (error: Error) => {
      toast({ title: "Falha ao criar vaga", description: error.message, variant: "destructive" });
    },
  });

  const approveJob = useMutation({
    mutationFn: async (id: string) => {
      return rpcClient.jobOpenings.approve({ id });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: orpc.jobOpenings.list.queryOptions().queryKey });
      toast({
        title: "Vaga aprovada",
        description: "A vaga foi publicada na página de carreiras.",
      });
    },
    onError: (error: Error) => {
      toast({ title: "Falha ao aprovar vaga", description: error.message, variant: "destructive" });
    },
  });

  const rejectJob = useMutation({
    mutationFn: async ({
      id,
      rejectionReason: reason,
    }: {
      id: string;
      rejectionReason: string;
    }) => {
      return rpcClient.jobOpenings.reject({ id, rejectionReason: reason });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: orpc.jobOpenings.list.queryOptions().queryKey });
      toast({ title: "Vaga rejeitada" });
      setRejectDialogOpen(false);
      setRejectJobId(null);
      setRejectionReason("");
    },
    onError: (error: Error) => {
      toast({
        title: "Falha ao rejeitar vaga",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const deleteJob = useMutation({
    mutationFn: async (id: string) => {
      return rpcClient.jobOpenings.delete({ id });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: orpc.jobOpenings.list.queryOptions().queryKey });
      toast({ title: "Vaga apagada", description: "A vaga foi removida com sucesso." });
      setDeleteDialogOpen(false);
      setDeleteJobData(null);
    },
    onError: (error: Error) => {
      toast({ title: "Falha ao apagar vaga", description: error.message, variant: "destructive" });
    },
  });

  const duplicateJob = async (jobId: string) => {
    setIsDuplicating(jobId);
    try {
      const prefill = await rpcClient.jobOpenings.getPrefill({ id: jobId });
      form.reset({
        title: prefill.title,
        generalResponsibilities: prefill.generalResponsibilities,
        desiredProfile: prefill.desiredProfile,
        compensation: prefill.compensation,
        jobLevel: prefill.jobLevel,
        department: prefill.department,
        hiringManagerId: prefill.hiringManagerId ?? "",
        salaryExpected: prefill.salaryExpected,
        interviewStages: prefill.interviewStages.map(
          (s: {
            stageName: string;
            stageOrder: number;
            durationMinutes: number;
            defaultInterviewerId: string | null;
            interviewerIds: string[];
            interviewerGroups: { employeeIds: string[]; schedulingLink: string | null }[];
          }) => ({
            stageName: s.stageName,
            stageOrder: s.stageOrder,
            durationMinutes: s.durationMinutes,
            defaultInterviewerId: s.defaultInterviewerId ?? "",
            interviewerIds: s.interviewerIds,
            interviewerGroups: s.interviewerGroups,
          }),
        ),
      });
      setIsOpen(true);
    } catch {
      toast({
        title: "Erro ao duplicar vaga",
        description: "Não foi possível carregar os dados da vaga.",
        variant: "destructive",
      });
    } finally {
      setIsDuplicating(null);
    }
  };

  const openDeleteDialog = (jobId: string, jobTitle: string) => {
    setDeleteJobData({ id: jobId, title: jobTitle });
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (!deleteJobData) return;
    deleteJob.mutate(deleteJobData.id);
  };

  const openRejectDialog = (jobId: string) => {
    setRejectJobId(jobId);
    setRejectionReason("");
    setRejectDialogOpen(true);
  };

  const handleRejectConfirm = () => {
    if (!rejectJobId || !rejectionReason.trim()) return;
    rejectJob.mutate({ id: rejectJobId, rejectionReason: rejectionReason.trim() });
  };

  const onSubmit = (data: JobFormValues) => {
    const stagesWithOrder = data.interviewStages.map((stage, idx) => ({
      ...stage,
      stageOrder: idx + 1,
      defaultInterviewerId: stage.defaultInterviewerId || undefined,
    }));
    createJob.mutate({ ...data, interviewStages: stagesWithOrder });
  };

  const addNewStage = () => {
    append({
      stageName: "",
      stageOrder: fields.length + 1,
      durationMinutes: 30,
      defaultInterviewerId: "",
      interviewerIds: [],
      interviewerGroups: [],
    });
  };

  const filteredJobs = jobs.filter(
    (job) => matchesSearch(job.title, searchQuery) || matchesSearch(job.department, searchQuery),
  );

  const pendingApprovalJobs = jobs.filter((job) => job.status === "pending_approval");

  const handleJobClick = (job: JobWithFavorites) => {
    setLocation(`/jobs/${job.id}`);
  };

  const JobCard = ({ job }: { job: JobWithFavorites }) => {
    const subtitle = [
      getDepartmentLabel(job.department),
      job.jobLevel && getJobLevelLabel(job.jobLevel),
    ]
      .filter(Boolean)
      .join(" · ");

    return (
      <InfoCard
        icon={<DepartmentIcon department={job.department} />}
        title={job.title}
        subtitle={subtitle}
        onClick={() => handleJobClick(job)}
        data-testid={`card-job-${job.id}`}
        menu={
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 flex-shrink-0"
              onClick={(e) => {
                e.stopPropagation();
                toggleFavorite.mutate({ jobId: job.id, isFavorited: job.isFavorited });
              }}
              data-testid={`button-favorite-job-${job.id}`}
            >
              <Star
                className={cn(
                  "h-4 w-4",
                  job.isFavorited
                    ? "fill-[hsl(var(--amber-state))] text-[hsl(var(--amber-state))]"
                    : "text-muted-foreground",
                )}
              />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 flex-shrink-0"
              onClick={(e) => {
                e.stopPropagation();
                togglePin.mutate({ jobId: job.id, isPinned: job.isPinned });
              }}
              data-testid={`button-pin-job-${job.id}`}
            >
              <Pin
                className={cn(
                  "h-4 w-4",
                  job.isPinned ? "fill-primary text-primary" : "text-muted-foreground",
                )}
              />
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 flex-shrink-0 text-muted-foreground"
                  onClick={(e) => e.stopPropagation()}
                  data-testid={`button-actions-job-${job.id}`}
                >
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                {canApproveJobs && job.status === "pending_approval" && (
                  <>
                    <DropdownMenuItem
                      onClick={() => approveJob.mutate(job.id)}
                      data-testid={`button-approve-job-${job.id}`}
                    >
                      <Check className="h-4 w-4 mr-2 text-[hsl(var(--forest-pulse))]" />
                      Aprovar
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => openRejectDialog(job.id)}
                      data-testid={`button-reject-job-${job.id}`}
                    >
                      <X className="h-4 w-4 mr-2 text-[hsl(var(--alert-red))]" />
                      Recusar
                    </DropdownMenuItem>
                  </>
                )}
                <DropdownMenuItem
                  onClick={() => duplicateJob(job.id)}
                  disabled={isDuplicating === job.id}
                  data-testid={`button-duplicate-job-${job.id}`}
                >
                  <Copy className="h-4 w-4 mr-2" />
                  {isDuplicating === job.id ? "Carregando..." : "Duplicar vaga"}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => openDeleteDialog(job.id, job.title)}
                  className="text-destructive focus:text-destructive"
                  data-testid={`button-delete-job-${job.id}`}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Apagar
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        }
        footer={
          <>
            <div className="flex items-center gap-4 text-sm text-muted-foreground">
              <div className="flex items-center gap-1.5">
                <Users className="h-3.5 w-3.5" />
                <span>{job.candidateCount ?? 0}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <UserRound className="h-3.5 w-3.5" />
                <span className="truncate max-w-[120px]">{job.hiringManager?.fullName || "-"}</span>
              </div>
            </div>
            <StatusBadge status={job.status} />
          </>
        }
      />
    );
  };

  const JobCardGrid = ({
    jobs,
    emptyMessage,
  }: {
    jobs: JobWithFavorites[];
    emptyMessage: string;
  }) => {
    if (jobs.length === 0) {
      return (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16">
            <div className="rounded-full bg-muted p-4 mb-4">
              <Briefcase className="h-8 w-8 text-muted-foreground/60" />
            </div>
            <p className="text-sm font-medium text-foreground mb-1">{emptyMessage}</p>
          </CardContent>
        </Card>
      );
    }
    return (
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {jobs.map((job) => (
          <JobCard key={job.id} job={job} />
        ))}
      </div>
    );
  };

  return (
    <div className="flex-1 space-y-6">
      <PageHeader title="Vagas" description="Gerenciar posições de recrutamento">
        {!isExternal && (
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setNotionImportOpen(true)}
              data-testid="button-import-notion"
            >
              <Download className="h-4 w-4 mr-2" />
              Importar do Notion
            </Button>
            <Dialog open={isOpen} onOpenChange={setIsOpen}>
              <DialogTrigger asChild>
                <Button data-testid="button-add-job">
                  <Plus className="h-4 w-4 mr-2" />
                  Nova Vaga
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Criar Vaga</DialogTitle>
                  <DialogDescription>
                    Preencha os dados da vaga e defina o processo de entrevistas.
                  </DialogDescription>
                </DialogHeader>
                <Form {...form}>
                  <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                    <FormField
                      control={form.control}
                      name="title"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Título da Vaga</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="Ex: Desenvolvedor Senior"
                              {...field}
                              data-testid="input-job-title"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="generalResponsibilities"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Responsabilidades Gerais</FormLabel>
                          <FormControl>
                            <Textarea
                              placeholder="Descreva as principais responsabilidades da posição..."
                              className="min-h-[80px]"
                              {...field}
                              data-testid="input-job-responsibilities"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="desiredProfile"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Perfil Desejado</FormLabel>
                          <FormControl>
                            <Textarea
                              placeholder="Descreva o perfil ideal do candidato..."
                              className="min-h-[80px]"
                              {...field}
                              data-testid="input-job-desired-profile"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="compensation"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Remuneração</FormLabel>
                          <FormControl>
                            <Textarea
                              placeholder="Descreva a faixa salarial, benefícios e bônus..."
                              className="min-h-[60px]"
                              {...field}
                              data-testid="input-job-compensation"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <div className="grid grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name="department"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Departamento</FormLabel>
                            <SearchableSelect
                              onValueChange={field.onChange}
                              defaultValue={field.value}
                              options={DEPARTMENTS.map((dept) => ({
                                value: dept.value,
                                label: dept.label,
                              }))}
                              placeholder="Selecione o departamento"
                              data-testid="select-job-department"
                            />
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="jobLevel"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Nível</FormLabel>
                            <SearchableSelect
                              onValueChange={field.onChange}
                              defaultValue={field.value}
                              options={JOB_LEVELS.map((level) => ({
                                value: level.value,
                                label: level.label,
                              }))}
                              placeholder="Selecione o nível"
                              data-testid="select-job-level"
                            />
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name="hiringManagerId"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Hiring Manager</FormLabel>
                            <SearchableSelect
                              onValueChange={field.onChange}
                              defaultValue={field.value}
                              options={(people || []).map((person) => ({
                                value: person.id,
                                label: person.fullName,
                              }))}
                              placeholder="Selecione o responsável"
                              data-testid="select-job-manager"
                            />
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="salaryExpected"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Salário Esperado (R$)</FormLabel>
                            <FormControl>
                              <Input
                                type="number"
                                placeholder="20000"
                                {...field}
                                data-testid="input-job-salary"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <div className="space-y-4">
                      <div>
                        <h3 className="text-sm font-medium">Etapas do Processo</h3>
                        <p className="text-xs text-muted-foreground">
                          Defina as etapas de entrevista e entrevistadores
                        </p>
                      </div>

                      <div className="space-y-3">
                        {fields.map((field, index) => (
                          <Card key={field.id} className="p-4">
                            <div className="flex items-start gap-4">
                              <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary/10 text-primary text-sm font-medium">
                                {index + 1}
                              </div>
                              <div className="flex-1 grid grid-cols-3 gap-3">
                                <FormField
                                  control={form.control}
                                  name={`interviewStages.${index}.stageName`}
                                  render={({ field }) => (
                                    <FormItem>
                                      <FormLabel className="text-xs">Nome da Etapa</FormLabel>
                                      <FormControl>
                                        <Input
                                          placeholder="Ex: Technical"
                                          {...field}
                                          data-testid={`input-stage-name-${index}`}
                                        />
                                      </FormControl>
                                      <FormMessage />
                                    </FormItem>
                                  )}
                                />
                                <FormField
                                  control={form.control}
                                  name={`interviewStages.${index}.durationMinutes`}
                                  render={({ field }) => (
                                    <FormItem>
                                      <FormLabel className="text-xs">Duração (min)</FormLabel>
                                      <FormControl>
                                        <Input
                                          type="text"
                                          inputMode="numeric"
                                          {...field}
                                          value={field.value ?? ""}
                                          onChange={(e) => {
                                            const raw = e.target.value.replace(/\D/g, "");
                                            field.onChange(raw === "" ? "" : parseInt(raw, 10));
                                          }}
                                          onBlur={() => {
                                            if (!field.value) field.onChange(30);
                                          }}
                                          data-testid={`input-stage-duration-${index}`}
                                        />
                                      </FormControl>
                                      <FormMessage />
                                    </FormItem>
                                  )}
                                />
                                <div className="col-span-3 space-y-3">
                                  <FormField
                                    control={form.control}
                                    name={`interviewStages.${index}.interviewerIds`}
                                    render={({ field }) => {
                                      const selectedIds = field.value || [];
                                      const selectedPeople =
                                        people?.filter((p) => selectedIds.includes(p.id)) || [];
                                      const hasInterviewersWithoutLink = selectedPeople.some(
                                        (p) => !p.schedulingLinks || p.schedulingLinks.length === 0,
                                      );
                                      const interviewersWithoutLink = selectedPeople.filter(
                                        (p) => !p.schedulingLinks || p.schedulingLinks.length === 0,
                                      );

                                      return (
                                        <FormItem>
                                          <FormLabel className="text-xs">
                                            Entrevistadores Individuais
                                          </FormLabel>
                                          <Popover>
                                            <PopoverTrigger asChild>
                                              <FormControl>
                                                <Button
                                                  variant="outline"
                                                  className="w-full justify-start font-normal"
                                                  data-testid={`select-stage-interviewers-${index}`}
                                                >
                                                  <Users className="h-4 w-4 mr-2 shrink-0" />
                                                  {selectedIds.length === 0
                                                    ? "Selecionar..."
                                                    : `${selectedIds.length} selecionado(s)`}
                                                </Button>
                                              </FormControl>
                                            </PopoverTrigger>
                                            <PopoverContent className="w-72 p-0" align="start">
                                              <div className="p-2 border-b">
                                                <div className="flex items-center gap-2 px-2">
                                                  <Search className="h-4 w-4 text-muted-foreground shrink-0" />
                                                  <input
                                                    type="text"
                                                    placeholder="Buscar entrevistador..."
                                                    className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
                                                    value={interviewerSearch[index] || ""}
                                                    onChange={(e) =>
                                                      setInterviewerSearch((prev) => ({
                                                        ...prev,
                                                        [index]: e.target.value,
                                                      }))
                                                    }
                                                    data-testid={`input-search-interviewers-${index}`}
                                                  />
                                                </div>
                                              </div>
                                              <div className="max-h-48 overflow-y-auto p-1">
                                                {people
                                                  ?.filter(
                                                    (p) =>
                                                      !interviewerSearch[index] ||
                                                      matchesSearch(
                                                        p.fullName,
                                                        interviewerSearch[index] || "",
                                                      ),
                                                  )
                                                  .map((person) => (
                                                    <label
                                                      key={person.id}
                                                      className="flex items-center gap-2 p-2 rounded hover:bg-muted cursor-pointer"
                                                    >
                                                      <Checkbox
                                                        checked={selectedIds.includes(person.id)}
                                                        onCheckedChange={(checked) => {
                                                          if (checked) {
                                                            field.onChange([
                                                              ...selectedIds,
                                                              person.id,
                                                            ]);
                                                          } else {
                                                            field.onChange(
                                                              selectedIds.filter(
                                                                (id: string) => id !== person.id,
                                                              ),
                                                            );
                                                          }
                                                        }}
                                                        data-testid={`checkbox-interviewer-${index}-${person.id}`}
                                                      />
                                                      <span className="text-sm flex-1">
                                                        {person.fullName}
                                                      </span>
                                                      {(!person.schedulingLinks ||
                                                        person.schedulingLinks.length === 0) && (
                                                        <AlertTriangle className="h-3 w-3 text-amber-500 shrink-0" />
                                                      )}
                                                    </label>
                                                  ))}
                                                {(!people || people.length === 0) && (
                                                  <p className="text-sm text-muted-foreground p-2">
                                                    Nenhum colaborador disponível
                                                  </p>
                                                )}
                                              </div>
                                            </PopoverContent>
                                          </Popover>
                                          {hasInterviewersWithoutLink && (
                                            <p className="text-xs text-amber-600 flex items-center gap-1">
                                              <AlertTriangle className="h-3 w-3" />
                                              {interviewersWithoutLink
                                                .map((p) => p.fullName)
                                                .join(", ")}{" "}
                                              sem scheduling link configurado
                                            </p>
                                          )}
                                          <FormMessage />
                                        </FormItem>
                                      );
                                    }}
                                  />

                                  <FormField
                                    control={form.control}
                                    name={`interviewStages.${index}.interviewerGroups`}
                                    render={({ field }) => {
                                      const groups = field.value || [];
                                      return (
                                        <FormItem>
                                          <FormLabel className="text-xs">
                                            Grupos de Entrevistadores
                                          </FormLabel>
                                          <div className="space-y-2">
                                            {groups.map(
                                              (
                                                group: {
                                                  employeeIds: string[];
                                                  schedulingLink: string | null;
                                                },
                                                gIdx: number,
                                              ) => (
                                                // biome-ignore lint/suspicious/noArrayIndexKey: interviewer groups lack stable unique id
                                                <Card key={gIdx} className="p-3 bg-muted/30">
                                                  <div className="space-y-2">
                                                    <div className="flex items-center justify-between">
                                                      <span className="text-xs font-medium">
                                                        Grupo {gIdx + 1}
                                                      </span>
                                                      <Button
                                                        type="button"
                                                        variant="ghost"
                                                        size="icon"
                                                        className="h-6 w-6 text-muted-foreground hover:text-destructive"
                                                        onClick={() => {
                                                          const newGroups = groups.filter(
                                                            (_: any, i: number) => i !== gIdx,
                                                          );
                                                          field.onChange(newGroups);
                                                        }}
                                                        data-testid={`button-remove-group-${index}-${gIdx}`}
                                                      >
                                                        <Trash2 className="h-3 w-3" />
                                                      </Button>
                                                    </div>
                                                    <Popover>
                                                      <PopoverTrigger asChild>
                                                        <Button
                                                          variant="outline"
                                                          size="sm"
                                                          className="w-full justify-start font-normal text-xs"
                                                          data-testid={`select-group-members-${index}-${gIdx}`}
                                                        >
                                                          <Users className="h-3 w-3 mr-1 shrink-0" />
                                                          {group.employeeIds.length === 0
                                                            ? "Selecionar membros..."
                                                            : `${group.employeeIds.length} membro(s)`}
                                                        </Button>
                                                      </PopoverTrigger>
                                                      <PopoverContent
                                                        className="w-72 p-0"
                                                        align="start"
                                                      >
                                                        <div className="max-h-48 overflow-y-auto p-1">
                                                          {people?.map((person) => (
                                                            <label
                                                              key={person.id}
                                                              className="flex items-center gap-2 p-2 rounded hover:bg-muted cursor-pointer"
                                                            >
                                                              <Checkbox
                                                                checked={group.employeeIds.includes(
                                                                  person.id,
                                                                )}
                                                                onCheckedChange={(checked) => {
                                                                  const newGroups = [...groups];
                                                                  if (checked) {
                                                                    newGroups[gIdx] = {
                                                                      ...newGroups[gIdx],
                                                                      employeeIds: [
                                                                        ...newGroups[gIdx]
                                                                          .employeeIds,
                                                                        person.id,
                                                                      ],
                                                                    };
                                                                  } else {
                                                                    newGroups[gIdx] = {
                                                                      ...newGroups[gIdx],
                                                                      employeeIds: newGroups[
                                                                        gIdx
                                                                      ].employeeIds.filter(
                                                                        (id: string) =>
                                                                          id !== person.id,
                                                                      ),
                                                                    };
                                                                  }
                                                                  field.onChange(newGroups);
                                                                }}
                                                              />
                                                              <span className="text-sm flex-1">
                                                                {person.fullName}
                                                              </span>
                                                            </label>
                                                          ))}
                                                        </div>
                                                      </PopoverContent>
                                                    </Popover>
                                                    {group.employeeIds.length > 0 && (
                                                      <div className="flex flex-wrap gap-1">
                                                        {group.employeeIds.map((empId: string) => {
                                                          const person = people?.find(
                                                            (p) => p.id === empId,
                                                          );
                                                          return person ? (
                                                            <Badge
                                                              key={empId}
                                                              variant="secondary"
                                                              className="text-xs"
                                                            >
                                                              {person.fullName}
                                                            </Badge>
                                                          ) : null;
                                                        })}
                                                      </div>
                                                    )}
                                                    <div className="space-y-1">
                                                      <Input
                                                        type="text"
                                                        placeholder="Link de agendamento do grupo (opcional)"
                                                        value={group.schedulingLink || ""}
                                                        onChange={(e) => {
                                                          const newGroups = [...groups];
                                                          newGroups[gIdx] = {
                                                            ...newGroups[gIdx],
                                                            schedulingLink: e.target.value,
                                                          };
                                                          field.onChange(newGroups);
                                                        }}
                                                        className="text-xs"
                                                        data-testid={`input-group-link-${index}-${gIdx}`}
                                                      />
                                                      {!group.schedulingLink && (
                                                        <p className="text-xs text-amber-600 flex items-center gap-1">
                                                          <AlertTriangle className="h-3 w-3" />
                                                          Sem link de agendamento — email não poderá
                                                          ser enviado
                                                        </p>
                                                      )}
                                                    </div>
                                                    {group.employeeIds.length > 0 &&
                                                      group.employeeIds.length < 2 && (
                                                        <p className="text-xs text-amber-600 flex items-center gap-1">
                                                          <AlertTriangle className="h-3 w-3" />
                                                          Grupo precisa de pelo menos 2 pessoas
                                                        </p>
                                                      )}
                                                  </div>
                                                </Card>
                                              ),
                                            )}
                                            <Button
                                              type="button"
                                              variant="outline"
                                              size="sm"
                                              className="w-full text-xs"
                                              onClick={() => {
                                                field.onChange([
                                                  ...groups,
                                                  { employeeIds: [], schedulingLink: null },
                                                ]);
                                              }}
                                              data-testid={`button-add-group-${index}`}
                                            >
                                              <Users className="h-3 w-3 mr-1" />
                                              Adicionar Grupo
                                            </Button>
                                          </div>
                                          <FormMessage />
                                        </FormItem>
                                      );
                                    }}
                                  />
                                </div>
                              </div>
                              {fields.length > 1 && (
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  className="text-muted-foreground hover:text-destructive"
                                  onClick={() => remove(index)}
                                  data-testid={`button-remove-stage-${index}`}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                          </Card>
                        ))}
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={addNewStage}
                        className="w-full"
                        data-testid="button-add-stage"
                      >
                        <Plus className="h-4 w-4 mr-1" />
                        Adicionar Etapa
                      </Button>
                      {form.formState.errors.interviewStages && (
                        <p className="text-sm text-destructive">
                          {form.formState.errors.interviewStages.message}
                        </p>
                      )}
                    </div>

                    <div className="flex justify-end gap-2 pt-4 border-t">
                      <Button type="button" variant="outline" onClick={() => setIsOpen(false)}>
                        Cancelar
                      </Button>
                      <Button
                        type="submit"
                        disabled={createJob.isPending}
                        data-testid="button-submit-job"
                      >
                        {createJob.isPending ? "Criando..." : "Criar Vaga"}
                      </Button>
                    </div>
                  </form>
                </Form>
              </DialogContent>
            </Dialog>
          </div>
        )}
      </PageHeader>

      <NotionImportDialog
        open={notionImportOpen}
        onOpenChange={setNotionImportOpen}
        positions={notionJobs?.notionPositions}
        isLoading={notionJobsLoading}
        importingNotionId={importingNotionId}
        isPending={importNotionJob.isPending}
        onImport={(notionId) => importNotionJob.mutate(notionId)}
      />

      {canApproveJobs && pendingApprovalJobs.length > 0 && (
        <Card className="bg-[hsl(var(--paper-white))] border border-[hsl(var(--sand-divider))] rounded-md">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base text-[hsl(var(--comp-coral))] font-semibold">
              <AlertCircle className="h-5 w-5" />
              Vagas aguardando aprovação ({pendingApprovalJobs.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {pendingApprovalJobs.map((job) => (
                <div
                  key={job.id}
                  className="flex items-center justify-between p-3 bg-background rounded-lg border"
                  data-testid={`pending-job-${job.id}`}
                >
                  <div className="flex items-center gap-4">
                    <div className="flex items-center justify-center w-10 h-10 rounded-full bg-[hsl(var(--amber-mist))]">
                      <Clock className="h-5 w-5 text-[hsl(var(--amber-state))]" />
                    </div>
                    <div>
                      <p className="font-medium">{job.title}</p>
                      <p className="text-sm text-muted-foreground">
                        {getDepartmentLabel(job.department)} - {getJobLevelLabel(job.jobLevel)} -{" "}
                        {formatCurrency(job.salaryExpected)}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Hiring Manager: {job.hiringManager?.fullName || "N/A"} | Criado:{" "}
                        {formatDate(job.createdAt)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openRejectDialog(job.id)}
                      disabled={rejectJob.isPending}
                      data-testid={`button-reject-pending-${job.id}`}
                    >
                      <X className="h-4 w-4 mr-1" />
                      Recusar
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => approveJob.mutate(job.id)}
                      disabled={approveJob.isPending}
                      data-testid={`button-approve-pending-${job.id}`}
                    >
                      <Check className="h-4 w-4 mr-1" />
                      Aprovar
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {isLoading ? (
        <div className="space-y-4">
          <div className="flex items-center justify-between gap-4">
            <Skeleton className="h-9 w-64 rounded-md" />
          </div>
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <Card key={i}>
                <CardContent className="p-5">
                  <div className="flex items-start gap-3 mb-6">
                    <Skeleton className="h-11 w-11 rounded-xl" />
                    <div className="flex-1 space-y-2">
                      <Skeleton className="h-5 w-3/4" />
                      <Skeleton className="h-4 w-1/2" />
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <Skeleton className="h-4 w-1/3" />
                    <Skeleton className="h-5 w-16 rounded-full" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar vagas..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
                data-testid="input-search-jobs"
              />
            </div>
          </div>

          <JobCardGrid jobs={filteredJobs} emptyMessage="Nenhuma vaga encontrada" />
        </div>
      )}

      <Dialog
        open={rejectDialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            setRejectDialogOpen(false);
            setRejectJobId(null);
            setRejectionReason("");
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rejeitar Vaga</DialogTitle>
            <DialogDescription>
              Informe o motivo da rejeição. Esta justificativa será enviada ao criador da vaga.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <label htmlFor="rejection-reason" className="text-sm font-medium">
                Motivo da rejeição *
              </label>
              <Textarea
                id="rejection-reason"
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="Descreva o motivo da rejeição..."
                rows={4}
                data-testid="textarea-rejection-reason"
              />
              {rejectionReason.length === 0 && (
                <p className="text-xs text-muted-foreground">
                  O motivo é obrigatório para rejeitar uma vaga.
                </p>
              )}
            </div>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setRejectDialogOpen(false);
                  setRejectJobId(null);
                  setRejectionReason("");
                }}
                data-testid="button-cancel-reject"
              >
                Cancelar
              </Button>
              <Button
                variant="destructive"
                onClick={handleRejectConfirm}
                disabled={!rejectionReason.trim() || rejectJob.isPending}
                data-testid="button-confirm-reject"
              >
                {rejectJob.isPending ? "Rejeitando..." : "Confirmar Rejeição"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={deleteDialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            setDeleteDialogOpen(false);
            setDeleteJobData(null);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Apagar Vaga</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja apagar a vaga <strong>{deleteJobData?.title}</strong>? Esta
              ação é irreversível e todos os dados relacionados (candidaturas, entrevistas, etapas)
              serão removidos permanentemente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-delete">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              disabled={deleteJob.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="button-confirm-delete"
            >
              {deleteJob.isPending ? "Apagando..." : "Apagar Vaga"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
